<template>
<div class="autocomplete | input" :class="{ '!border-transparent': disabled, 'has-focus': HasFocus }" data-component="Autocomplete.vue">
	<input
		:id="InputName"
		ref="input"
		v-model="Input"
		:type="type"
		:placeholder="Placeholder"
		required
		:style="{ 'padding-left': EmblemWidth }"
		@focus="OnFocus"
		@keyup="OnInput"
		@blur="OnBlur"
		@keydown="OnNavigate"
		:data-test="DataTest"
		:disabled="disabled"
		:class="{ '!bg-neutral-design-50': disabled }"
	>
	<div ref="list" class="suggestions-list">
		<template v-for="(s, i) in SuggestionsList">
			<a
				v-if="s.Label"
				:key="i"
				:data-value="s.Value"
				@click="OnSelect(s, $event)"
				v-html="s.Label"
			/>
		</template>
	</div>
</div>
</template>

<script lang="ts">
// @vuese
// Komponent odpowiedzialny za kontrolkę autouzupełniania.
// @group Base Components | Forms
export default {
	name: 'Autocomplete',
	props: {
		DataTest: { default: undefined }, 
		modelValue: { default: '' },
		InputName: { default: '' },
		Suggestions: {},
		Autocomplete: { default: '' }, // {{q}}
		AutocompleteSuggestions: {},
		Placeholder: { default: '' },
		Autoselect: { default: true },
		IgnoreValues: { default: () => [] },
		EmblemWidth: { default: '' },
		focus: Boolean,
		IgnoreCase: {
			type: Boolean,
			default: false,
		},
		type: { default: 'text', type: String },
		disabled: { default: false, type: Boolean },
	},
	emits: ['update:modelValue', 'process', 'blur', 'select', 'focus-changed'],
	data() {
		return {
			Value: '',
			Input: '',
			InputSuggestions: [],
			SelectedIndex: null,
			WritableSuggestions: this.Suggestions,
			HasFocus: false,
		};
	},
	computed: {
		SuggestionsList() {
			return (this.Input) ? this.InputSuggestions : (this.AutocompleteSuggestions || this.WritableSuggestions);
		},
	},
	watch: {
		Suggestions(n) {
			this.WritableSuggestions = n;
		},
		modelValue(n) {
			this.Value = n[0];
			this.Text = this.Input = n[1];
		},
	},
	mounted() {
		if (this.focus) {
			this.focusInput();
		}
	},
	created() {
		this.Value = this.modelValue[0];
		this.Text = this.Input = this.modelValue[1];
	},
	methods: {
		focusInput() {
			this.$refs.input.focus();
		},
		OnFocus() {
			this.$emit('focus-changed', true);
			this.HasFocus = true;
			if (!this.InputSuggestions.length && this.Input) {
				this.OnInput();
			}
		},
		OnInput(e) {
			if (e && [38, 40, 13].includes(e.keyCode)) { return; }

			// BUG: Na Androidach, jeżeli nie został wykonany autocomplete, v-model się nie zaktualizuje.
			this.Input = this.$refs.input.value;

			const $list = this.$refs.list;
			$list.scrollTop = 0;

			const $selected = $list.querySelector('.is-active');
			if ($selected) { $selected.classList.remove('is-active'); }
			this.SelectedIndex = null;

			if (this.AutocompleteSuggestions) {
				this.$emit('update:modelValue', [this.Value, this.Input]);
				this.InputSuggestions = this.AutocompleteSuggestions.filter((suggestion) => {
					if (this.IgnoreCase) {
						suggestion.Label = suggestion.Label.charAt(0).toUpperCase() + suggestion.Label.slice(1);
						this.Input = this.Input.charAt(0).toUpperCase() + this.Input.slice(1);
					}
					return suggestion.Label.indexOf(this.Input) >= 0;
				});
				this.$emit('process', this.InputSuggestions, this);
			} else if (this.Autocomplete && this.Input) {
				let autoUrl = this.Autocomplete;
				let viaZip = false;
				if (autoUrl === 'Location') {
					viaZip = !Number.isNaN(this.Input[0]);
					autoUrl = `/common/dictionary/zip/search${viaZip ? '-by-zip' : ''}.ofer?query={{q}}&cc=PL`;
				}
				// /common/dictionary/zip/search-by-zip.ofer?query={{q}}&cc=PL
				fetch(autoUrl.replace('{{q}}', this.Input))
					.then((rsvp) => rsvp.json())
					.then((rsvp) => {
						const process = [];
						if (this.Autocomplete.indexOf('autocomplete-form') > -1) {
							for (const s of rsvp) {
								if (this.IgnoreValues.indexOf(s.idCategory) > -1) { continue; }
								process.push({
									Value: s.idCategory,
									Label: s.categoryName + (s.service ? '' : '<small class="ml-1 opacity-75">Sprzedaż</small>'),
									Input: s.categoryName,
									Element: s,
								});
							}
						}

						if (this.Autocomplete.indexOf('Location') > -1) {
							for (const s of rsvp) {
								if (this.IgnoreValues.indexOf(s.idCategory) > -1) { continue; }
								process.push({
									Value: {
										country: ['locationPl', 'Polska'],
										region: [s.idLocation, s.location],
										district: [s.idDistrict, s.district],
										city: [s.zip, s.city],
										id: s.id,
									},
									Label: viaZip ? `<small class="mr-1 opacity-75">${s.zip}</small> ${s.city}` : s.city,
									Input: s.city,
									Element: s,
								});
							}
						}

						if (this.Autocomplete.indexOf('search-by-zip') > -1) {
							for (const s of rsvp) {
								if (this.IgnoreValues.indexOf(s.idCategory) > -1) { continue; }
								process.push({
									Value: {
										country: ['locationPl', 'Polska'],
										region: [s.idLocation, s.location],
										district: [s.idDistrict, s.district],
										city: [s.zip, s.city],
										id: s.id,
									},
									Label: `<small class="mr-1 opacity-75">${s.zip}</small> ${s.city}`,
									Input: s.city,
									Element: s,
								});
							}
						}

						this.InputSuggestions = process;
						this.$emit('process', rsvp, this);
						this.$emit('update:modelValue', [this.Value, this.Input]);
					});
			} else {
				this.$emit('process', [], this);
				this.$emit('update:modelValue', [this.Value, this.Input]);
			}
		},
		OnBlur() {
			this.$emit('focus-changed', false);
			window.setTimeout(() => { this.HasFocus = false; }, 100);

			if (!this.Autoselect || this.Value === -1) { return; }
			const $selected = this.SelectFirst();
			this.$emit('blur', [null, this.Input]);
			if ($selected) {
				$selected.click();
			} else {
				this.$emit('update:modelValue', [null, this.Input]);
			}
		},
		OnNavigate(e) {
			if (!([38, 40, 13].includes(e.keyCode))) { return; }
			e.preventDefault();
			e.stopPropagation();

			let direction = (e.keyCode === 38 ? -1 : 1);
			if (e.keyCode === 13) { direction = 0; }

			const $input = this.$refs.input;
			const $list = this.$refs.list;
			let $selected = this.SelectFirst();

			if (direction === 0) {
				if ($selected) { $selected.click(); }
				$input.blur();
			} else {
				if (!$selected) {
					$selected = direction > 0 ? $list.firstElementChild : $list.lastElementChild;
				} else {
					$selected.classList.remove('is-active');
					$selected = direction > 0 ? $selected.nextElementSibling : $selected.previousElementSibling;
				}

				if ($selected) {
					$selected.classList.add('is-active');
					let index = Array.prototype.slice.call($list.children);
					index = index.indexOf($selected);
					$list.scrollTop = $selected.offsetHeight * (index - 2);
				}
			}
		},
		OnSelect(s) {
			this.Value = s.Value;
			this.Input = s.Input || s.Label;
			this.$emit('update:modelValue', [this.Value, this.Input]);
			this.$emit('select', s);
		},
		SelectFirst() {
			const $list = this.$refs.list;
			let $selected = $list?.querySelector('.is-active');
			if (!$selected && $list.children.length) {
				$selected = $list.children[0];
				$selected.classList.add('is-active');
			}
			return $selected;
		},
	},
};
</script>
